import React, { useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import Moment from 'moment';
import { Box } from '@mui/material';

// dev-express table system
import { useQueryParams } from '../../../components/providers/QueryParamsProvider';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { useSavedSettings } from '../../../components/tables/dev-express/hooks/useSavedSettings';
import { useBackfill } from '../../../components/tables/dev-express/hooks/useBackfill';
import { useTableState } from '../../../components/tables/dev-express/hooks/useTableState';
import { useQueryHandlers } from '../../../components/tables/dev-express/hooks/useQueryHandlers';
import { useUiSettingsAutosave } from '../../../components/tables/dev-express/hooks/useUiSettingsAutosave';
import { PrimaryToolbar } from '../../../components/tables/dev-express/table-components/toolbar/PrimaryToolbar';
import { SecondaryToolbar } from '../../../components/tables/dev-express/table-components/toolbar/SecondaryToolbar';
import { TableSearch } from '../../../components/tables/dev-express/table-components/toolbar/TableSearch';
import { ColumnChooser } from '../../../components/tables/dev-express/table-components/toolbar/ColumnChooser';
import { RefreshButton } from '../../../components/tables/dev-express/table-components/toolbar/RefreshButton';
import { TableStateProvider } from '../../../components/tables/dev-express/TableStateContext';
import { BaseTable } from '../../../components/tables/dev-express/BaseTable';
import { RemotePagingPlugin } from '../../../components/tables/dev-express/plugins/RemotePagingPlugin';
import { ColumnResizingPlugin } from '../../../components/tables/dev-express/plugins/ColumnResizingPlugin';
import type { ColumnConfig } from '../../../components/tables/dev-express/types/table.types';

// existing helpers / components / apis
import { DocumentDownloadButton } from '../../../components/fileUploader/DocumentDownloadButton';
import { useDeleteDocument } from '../../../components/fileUploader/useDeleteDocument';
import { useFileTypeIcons } from '../../../components/fileUploader/useFileTypeIcons';
import { withUploadFeatureDocuments } from '../../../components/fileUploader/withUploadFeatureDocuments';
import { UrlDateRangePicker } from '../../../components/inputs/DateRangePicker';
import { getPHDocuments } from '../../../api/DocumentsService';
import { DocumentPreviewModalConfig } from '../../../components/fileUploader/DocumentPreviewModal';
import { Modal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import { DoctypeDropdown } from './DoctypeDropdown';
import { withUWDocDependencies } from './withUWDocDependencies';
import { hasUrl } from '../../../utils/appUtils';
import { PubSub } from '../../../utils/eventUtils';
import {
  FORMAT_DATE_AND_TIME,
  toUniversalUtcDate,
} from '../../../utils/date.utils';
import classes from './Documents.module.css';

const TABLE_ID = 'PhDocuments';
const BACKFILL = { order: 'asc', orderBy: 'docName', size: 25, page: 0 };
const isCarrier = hasUrl('carrier/');

const DocumentsListing = ({ readOnly, docTypes }: any) => {
  const { query } = useQueryParams();
  const handleAPIError = useAPIErrorHandler();
  const deleteDocument = useDeleteDocument();
  const queryClient = useQueryClient();

  const columnConfig = useColumnConfig();
  const { uiSettings } = useSavedSettings(TABLE_ID, columnConfig);
  useBackfill(BACKFILL, uiSettings);

  const { queryHandlers } = useQueryHandlers(query);
  const { state, columns, handlers } = useTableState(columnConfig, {
    uiSettings,
  });

  useUiSettingsAutosave(
    TABLE_ID,
    {
      columns,
      columnOrder: state.columnOrder,
      ...query,
    },
    ['before', 'after']
  );

  const { data, isFetching } = useQuery({
    queryKey: [TABLE_ID, query],
    queryFn: () => getPHDocuments({ ...query }),
    select: (response: any) => ({
      rows: response.data.content,
      totalCount: response.data.totalElements,
    }),
    onError: handleAPIError('Unable to retrieve documents.'),
  });

  const onRefetch = () => {
    queryClient.invalidateQueries([TABLE_ID]);
  };

  // preserve legacy `delayedEvent('table-refetch', ...)` refresh triggers
  useEffect(() => {
    const sub = PubSub.subscribe('table-refetch', (id: any) => {
      if (id === TABLE_ID) {
        queryClient.invalidateQueries([TABLE_ID]);
      }
    });
    return () => {
      sub.remove();
    };
  }, [queryClient]);

  const context = useMemo(
    () => ({ readOnly, docTypes, deleteDocument }),
    [readOnly, docTypes, deleteDocument]
  );

  const mergedState = useMemo(() => ({ ...state, ...query }), [query, state]);
  const mergedHandlers = useMemo(
    () => ({ ...handlers, ...queryHandlers }),
    [handlers, queryHandlers]
  );

  const title = isCarrier ? 'Reports' : 'Documents';

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        style={{ marginBottom: 15 }}
      >
        <h1 className="table-heading" style={{ marginTop: 0 }}>
          {title}
        </h1>
      </Box>

      <PrimaryToolbar>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Box
            display="flex"
            gap="8px"
            justifyContent="flex-end"
            alignItems="center"
            style={{ width: '50%' }}
          >
            <TableSearch placeholder="Search" />
            <UrlDateRangePicker maxDate={new Date()} inlineLabel />
          </Box>
        </Box>
      </PrimaryToolbar>

      <TableStateProvider
        state={mergedState}
        handlers={mergedHandlers}
        columns={columns}
        rows={data?.rows ?? []}
        tableId={TABLE_ID}
        loading={isFetching}
        context={context}
      >
        <SecondaryToolbar justifyContent="flex-end">
          <ColumnChooser />
          <RefreshButton onClick={onRefetch} loading={isFetching} />
        </SecondaryToolbar>

        <BaseTable>
          <RemotePagingPlugin totalCount={data?.totalCount ?? 0} />
          <ColumnResizingPlugin />
        </BaseTable>
      </TableStateProvider>
    </>
  );
};

/**
 * The file-type icon, which opens the document preview.
 *
 * <p>The icon is the only preview affordance the documents grids have -- the
 * Name column is plain text on this table and on the UW one. This table shipped
 * without any preview at all: the UW table wraps its icon in
 * `DocumentPreviewer`, this one rendered the bare SVG, so a policyholder could
 * only ever download a document to read it (FIR-21146).
 *
 * <p>It opens the modal from a `<button>` rather than reaching for
 * `DocumentPreviewer`: that helper's `aria-hidden` div is unreachable by
 * keyboard and silent to screen readers, which is why ClaimNoteDetail already
 * moved off it. The preview fetch is the same either way -- `downloadDocument`,
 * the endpoint the Download column on this table already calls.
 */
const FileTypeCell = ({ row }: Readonly<{ row: any }>) => {
  const icon = useFileTypeIcons(row.fileType, undefined, row.docName);

  const openPreview = () => {
    Modal.show(DocumentPreviewModalConfig, { data: { docId: row.id } });
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
    >
      {row.id ? (
        <button
          type="button"
          className={classes.previewButton}
          aria-label={`Preview ${row.docName}`}
          onClick={openPreview}
        >
          {icon}
        </button>
      ) : (
        icon
      )}
      {row.fileType}
    </Box>
  );
};

const DocTypeCell = ({
  row,
  docTypes,
}: Readonly<{ row: any; docTypes: any }>) => (
  <DoctypeDropdown
    docTypes={docTypes}
    key={row.id}
    data={row}
    disabled={!!row.systemGenerated}
  />
);

const DownloadCell = ({ row }: Readonly<{ row: any }>) => (
  <DocumentDownloadButton
    id={row.id}
    name={row.docName}
    type={row.fileType?.toLowerCase()}
  />
);

const useColumnConfig = (): ColumnConfig => ({
  docName: { name: 'docName', title: 'Name' },
  fileType: {
    name: 'fileType',
    title: 'File Type',
    // Size tokens, not pixels: 50px truncated both the header and the
    // icon + extension cell, and a fixed pixel width is excluded from the
    // full-width fill pass (see FLEXIBLE_WIDTH_TOKENS in table.utils).
    width: 's',
    getCellValue: (row: any) => <FileTypeCell row={row} />,
  },
  docType: {
    name: 'docType',
    title: 'Doc Type',
    // Holds the doc-type dropdown, so it needs the wider content token.
    width: 'm',
    getCellValue: (row: any, ctx: any) => (
      <DocTypeCell row={row} docTypes={ctx?.docTypes} />
    ),
  },
  systemGenerated: {
    name: 'systemGenerated',
    title: 'System Generated',
    getCellValue: (row: any) => (row.systemGenerated ? 'True' : 'False'),
    csv: (row: any) => (row.systemGenerated ? 'True' : 'False'),
  },
  modified: {
    name: 'modified',
    title: 'Last Updated',
    getCellValue: (row: any) =>
      row.modified ? Moment(row.modified).fromNow() : '-',
  },
  uploadTime: {
    name: 'uploadTime',
    title: 'Uploaded',
    show: false,
    getCellValue: (row: any) =>
      toUniversalUtcDate(row.uploadTime.timestamp, {
        format: FORMAT_DATE_AND_TIME,
      }),
  },
  actions: {
    name: 'actions',
    title: 'Download',
    canToggle: false,
    sort: false,
    width: 75,
    getCellValue: (row: any) => <DownloadCell row={row} />,
  },
});

export const Documents = compose(
  withUWDocDependencies(),
  withUploadFeatureDocuments(65, 'docs')
)(DocumentsListing);

export default Documents;
